import { Validators } from '@utils/validators'
import { VALIDATION_MESSAGES } from '@constants'

/**
 * Reusable validator for required fields that must not be empty
 */
const createRequiredFieldValidator = (message: string = 'This field is required.') => ({
  validate: (v: any) => {
    const value = String(v ?? '')
    return value.trim() !== ''
  },
  message,
})

/**
 * Result of field validation
 */
export interface FieldValidationResult {
  isValid: boolean
  message: string
}

/**
 * Generic field validator interface that works with any form data structure
 */
export interface FieldValidator<TFormData = any> {
  /** If omitted, field is considered always required */
  required?: (formData: TFormData) => boolean
  /** Validation function */
  validate: (value: any, formData: TFormData) => boolean
  /** Default error message */
  message: string
}

/**
 * Unified field validators for ALL account forms (Registration, SignIn, Help flows).
 * This consolidates validation logic from multiple locations into a single source of truth.
 */
export const fieldValidators: Record<string, FieldValidator> = {
  // Owner/Member Number
  ownerNumber: {
    validate: (v) => Validators.isMemberNumber(String(v ?? '')),
    message: VALIDATION_MESSAGES.OWNER_NUMBER_REQUIRED,
  },

  // Personal Information
  firstName: {
    required: (fd: any) => !fd.registerAsCompany?.value,
    validate: (v) => Validators.isFirstName(String(v ?? '')),
    message: VALIDATION_MESSAGES.FIRST_NAME_REQUIRED,
  },
  lastName: {
    required: (fd: any) => !fd.registerAsCompany?.value,
    validate: (v) => Validators.isLastName(String(v ?? '')),
    message: VALIDATION_MESSAGES.LAST_NAME_REQUIRED,
  },
  companyName: {
    required: (fd: any) => !!fd.registerAsCompany?.value,
    validate: (v) => Validators.isCompanyName(String(v ?? '')),
    message: VALIDATION_MESSAGES.COMPANY_NAME_REQUIRED,
  },

  // Verification Methods
  verificationMethod: {
    validate: (v) => v === 'phone' || v === 'contract' || v === 'email' || v === 'username',
    message: VALIDATION_MESSAGES.VERIFICATION_METHOD_REQUIRED,
  },
  phone: {
    required: (fd: any) => fd.verificationMethod?.value === 'phone',
    validate: (v, fd: any) => Validators.isPhone(String(v ?? ''), String(fd.phoneCountry?.value ?? 'USA')),
    message: VALIDATION_MESSAGES.PHONE_REQUIRED,
  },
  phoneNumber: {
    required: (fd: any) => fd.verificationMethod?.value === 'phone',
    validate: (v, fd: any) => Validators.isPhone(String(v ?? ''), String(fd.phoneCountry?.value ?? 'USA')),
    message: VALIDATION_MESSAGES.PHONE_REQUIRED,
  },
  contractNumber: {
    required: (fd: any) => fd.verificationMethod?.value === 'contract',
    validate: (v) => Validators.isContractNumber(String(v ?? '')),
    message: VALIDATION_MESSAGES.CONTRACT_NUMBER_REQUIRED,
  },

  // Authentication
  email: {
    validate: (v) => {
      const value = String(v ?? '')
      // If empty, it's invalid (required field)
      if (!value || value.trim() === '') return false
      // If provided, validate email format
      return Validators.isValidEmail(value)
    },
    message: 'This field is required.',
  },
  emailAddress: {
    validate: (v) => {
      const value = String(v ?? '')
      // If empty, it's invalid (required field)
      if (!value || value.trim() === '') return false
      // If provided, validate email format
      return Validators.isValidEmail(value)
    },
    message: 'This field is required.',
  },
  country: createRequiredFieldValidator(),
  addressLine1: createRequiredFieldValidator(),
  city: createRequiredFieldValidator(),
  userName: {
    validate: (v) => {
      const value = String(v ?? '')
      if (!value) return false
      const meetsLength = value.length >= 6
      const startsWithLetter = /^[A-Za-z].*/.test(value)
      return meetsLength && startsWithLetter
    },
    message: VALIDATION_MESSAGES.USERNAME_INVALID,
  },
  username: {
    validate: (v) => {
      const value = String(v ?? '')
      if (!value) return false
      // For sign-in, username can be shorter (3 chars minimum)
      return value.length >= 3 && value.length <= 50 && /^[a-zA-Z0-9_-]+$/.test(value)
    },
    message: VALIDATION_MESSAGES.USERNAME_INVALID,
  },
  password: {
    validate: (v) => Validators.isStrongPassword(String(v ?? ''), 8),
    message: VALIDATION_MESSAGES.PASSWORD_INVALID,
  },
  newPassword: {
    validate: (v, formData: any) => {
      const password = String(v ?? '')
      // Use Okta password policy if available, otherwise fallback to default
      const passwordPolicy = formData?.passwordPolicy
      if (passwordPolicy) {
        return Validators.isStrongPassword(password, passwordPolicy.minLength || 8)
      }
      return Validators.isStrongPassword(password, 8)
    },
    message: VALIDATION_MESSAGES.PASSWORD_INVALID,
  },
  confirmPassword: {
    validate: (v, fd: any) => {
      const newPassword = fd.newPassword?.value || fd.password?.value
      return v === newPassword
    },
    message: VALIDATION_MESSAGES.PASSWORDS_DO_NOT_MATCH,
  },

  // Verification Code
  verificationCode: {
    validate: (v) => {
      const value = String(v ?? '').trim()
      return value.length >= 4 && value.length <= 8 && /^\d+$/.test(value)
    },
    message: VALIDATION_MESSAGES.VERIFICATION_CODE_REQUIRED,
  },

  // Optional fields
  rememberMe: {
    validate: () => true,
    message: '',
  },
  registerAsCompany: {
    validate: () => true,
    message: '',
  },
  phoneCountry: {
    validate: () => true,
    message: '',
  },
  stateProvince: {
    required: () => true, // State/Province is required
    validate: (v) => {
      const value = String(v ?? '')
      // Must not be empty
      return value.trim() !== ''
    },
    message: 'This field is required.',
  },
  postalCode: {
    required: () => false, // Postal code is optional
    validate: (v, fd: any) => {
      const value = String(v ?? '')
      // If empty, it's valid (since it's optional)
      if (!value || value.trim() === '') return true

      // Handle both string country codes and object with .value property
      const countryCode = String(fd.country?.value ?? fd.country ?? 'US')

      // If provided, validate it (isPostalCode already handles both ISO-2 and ISO-3 codes)
      return Validators.isPostalCode(value, countryCode)
    },
    message: VALIDATION_MESSAGES.POSTAL_CODE_REQUIRED,
  },
}

export interface ValidateFieldOptions {
  /** When provided, used as the error message when validation fails (e.g. from AuthLabelsContext.getValidationMessage). */
  getValidationMessage?: (fieldName: string) => string
}

/**
 * Validate a single field against the unified validation rules.
 * Works with any form data structure (Registration, SignIn, etc.)
 *
 * @param fieldName - Name of the field to validate
 * @param value - Current value of the field
 * @param formData - Complete form data for conditional validation
 * @param options - Optional message resolver (e.g. getValidationMessage from AuthLabelsContext)
 * @returns Validation result with isValid flag and error message
 */
export const validateField = <TFormData = any>(
  fieldName: string,
  value: any,
  formData: TFormData,
  options?: ValidateFieldOptions
): FieldValidationResult => {
  const validator = fieldValidators[fieldName]

  if (!validator) {
    // No validator defined: field is always valid
    return { isValid: true, message: '' }
  }

  // Check if field is required
  const required = validator.required ? validator.required(formData) : true

  const isEmptyValue = (val: any): boolean => {
    if (val == null || val === '') {
      return true
    }
    // supports the special case for phone number input
    if (typeof val === 'object' && !Array.isArray(val) && 'inputValue' in val) {
      return val.inputValue == null || val.inputValue === ''
    }
    return false
  }

  // If field is not required and value is empty, it's valid
  if (!required && isEmptyValue(value)) {
    return { isValid: true, message: '' }
  }

  // Run validation
  const passes = validator.validate(value, formData)
  const message = passes ? '' : (options?.getValidationMessage?.(fieldName) ?? validator.message)

  return {
    isValid: passes,
    message,
  }
}

/**
 * Validate multiple fields at once
 *
 * @param formData - Complete form data
 * @param fieldNames - Array of field names to validate
 * @returns Array of validation results
 */
export const validateFields = <TFormData = any>(
  formData: TFormData,
  fieldNames: string[],
  options?: ValidateFieldOptions
): Record<string, FieldValidationResult> => {
  const results: Record<string, FieldValidationResult> = {}

  fieldNames.forEach((fieldName) => {
    const fieldValue = (formData as any)[fieldName]?.value ?? (formData as any)[fieldName]
    results[fieldName] = validateField(fieldName, fieldValue, formData, options)
  })

  return results
}

/**
 * Check if all specified fields are valid
 *
 * @param formData - Complete form data
 * @param fieldNames - Array of field names to check
 * @returns True if all fields are valid
 */
export const areFieldsValid = <TFormData = any>(formData: TFormData, fieldNames: string[]): boolean => {
  const results = validateFields(formData, fieldNames)
  return Object.values(results).every((result) => result.isValid)
}
